import React, {SyntheticEvent, useEffect, useState} from 'react';
import {Button, Form, InputGroup, Modal, OverlayTrigger, Popover} from 'react-bootstrap';
import Spinner from 'react-bootstrap/Spinner';
import PetrolStationService from 'services/petrol-station.service';
import Select, { ValueType } from 'react-select';
import {ISimplifiedPetrolStation} from "models/petrol-station";
import {toast} from "react-toastify";
import {ICustomerCardType} from '../../../models/customer-card';
import {CardCategory, CustomerApplication, CustomerApplicationSigner, CustomerApplicationType, PickupType, SignerRepresentative, SignerType, withCreatedApplication} from '../../../models/customer-application';
import {EMPTY_CUSTOMER_REPRESENTATIVES, SelectableRepresentative, toSelectableRepresentatives} from '../../../models/customer-representative';
import CustomerService from '../../../services/customer.service';
import {areRepresentativeSignersValid, AuthorizedSignerForm, isAuthorizedSignerValid, SignerSection, toAuthorizedSignerForm, toSelectedRepresentatives, toSignerType} from '../SignerSection';
import {InitContextProps, useAuthContext} from '../../../services/context/authentication.context';
import CustomerApplicationService from '../../../services/customer-application.service';
import {SwitchButton, SwitchButtonState} from '../../SwitchButton/SwitchButton';
import { PaymentCardSection } from '../PaymentCardSection';
import { CustomerLocation, isCustomerLocationRequired } from '../../../models/customer-location';
import { CustomerLocationService } from '../../../services/customer-location.service';
import DpdParcelShopService from '../../../services/dpd-parcel-shop.service';
import {DpdParcelShopResponseModel} from '../../../models/dpd-parcel-shop';
import { PickupOption } from 'models/pickup-option';
import { initialCardsStatePayment, cardCategoryLabels, CARD_CATEGORY_TO_ICON, CARD_APPLICATION_MONTHLY_LIMIT_REGEX } from 'common/constants/cardApplication';
import { ModifyCustomerCardConfirmModal } from './ModifyCustomerCard/ModifyCustomerCardConfirmModal';
import { isEmailValidAndPresent, isPhoneNumber, validateBankAccountNumber } from 'helpers/validators';
import { handleTextInputNumberChange } from 'helpers';
import { isNullOrUndefined } from 'common/data-util';

interface CreditApplicationModalProps {
    handleModalHide: () => void;
    application?: CustomerApplication;
    onSuccess?: () => void;
}

interface CardForm {
    cardName: string;
    monthlyLimit?: string;
    cardCategory?: CardCategory;
    isVirtual: boolean;
    customerLocationId?: number;
}

interface ICreditApplicationModalForm {
    contactPhone: string;
    contactEmail: string;
    invoiceEmail: string;
    eInvoice: boolean;
    bankAccountNumber: string;
    purchaseVolume: string;
    creditLimit: string;
    paymentDay: string;
    invoicesPerMonth: number;
    pickupLocation: string;
    pickupLocationId: string | number;
    pickupType: PickupType;
    sameDeliveryContact: boolean;
    recipientEmail: string;
    recipientPhone: string;
    cards: Record<CardCategory, CardForm[]>;
    signerType: SignerType;
    selectedSignerRepresentatives: SignerRepresentative[];
    authorizedSigner: AuthorizedSignerForm;
}

export const getPaymentCards = (application: CustomerApplication | undefined): Record<CardCategory, CardForm[]> => {
    if (application?.customerApplicationCards?.length) {
        const result = {
            ...initialCardsStatePayment
        };

        application.customerApplicationCards.forEach(group => {
            const category = group.cardCategory as CardCategory;
            if (category && result[category] !== undefined) {
                result[category] = group.cards.map(card => ({
                    cardName: card.cardName ?? '',
                    monthlyLimit: card.cardLimit != null ? String(card.cardLimit) : undefined,
                    cardCategory: card.cardCategory as CardCategory,
                    isVirtual: card.isVirtual!,
                    customerLocationId: card.customerLocationId
                }));
            }
        });

        return result;
    }
    
    return initialCardsStatePayment;
};

export function CreditApplicationModal(props: Readonly<CreditApplicationModalProps>) {
    const { state, dispatch }: InitContextProps = useAuthContext();
    const { customerId, customer, user } = state;

    const [stations, setStations] = useState<ISimplifiedPetrolStation[]>([])
    const [isLoadingStations, setIsLoadingStations] = useState(false);
    const [customerLocations, setCustomerLocations] = useState<CustomerLocation[]>([]);
    const [dpdParcelShops, setDpdParcelShops] = useState<DpdParcelShopResponseModel[]>([]);
    const [isLoadingDpdParcelShops, setIsLoadingDpdParcelShops] = useState(false);

    const [saving, setSaving] = useState(false);
    const [savingError, setSavingError] = useState(false);
    const [validated, setValidated] = useState(false);
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);

    const [form, setForm] = useState<ICreditApplicationModalForm>({
        contactPhone: props.application?.contact?.phone ?? customer?.phone ?? customer?.mobileNumber ?? '',
        contactEmail: props.application?.contact?.email ?? customer.email,
        invoiceEmail: props.application?.invoiceEmail ?? '',
        eInvoice: props.application?.isEInvoice ?? customer.eInvoice,
        bankAccountNumber: props.application?.bankAccountNumber ?? customer.bankAccountNumber,
        purchaseVolume: props.application?.purchaseVolume ?? '',
        creditLimit: props.application?.creditLimit ?? '',
        paymentDay: props.application?.paymentDay ?? '14',
        invoicesPerMonth: props.application?.invoicesPerMonth ?? 1,
        pickupLocation: props.application?.pickupLocation ?? '',
        pickupLocationId: props.application ? (props.application.pickupType === 'STATION' ? Number(props.application.pickupLocationId) : props.application.pickupLocationId) : '',
        pickupType: (props.application?.pickupType as PickupType) ?? 'STATION',
        sameDeliveryContact: !props.application?.recipientEmail && !props.application?.recipientPhone,
        recipientEmail: props.application?.recipientEmail ?? '',
        recipientPhone: props.application?.recipientPhone ?? '',
        cards: getPaymentCards(props.application),
        signerType: toSignerType(props.application?.signers),
        selectedSignerRepresentatives: toSelectedRepresentatives(props.application?.signers),
        authorizedSigner: toAuthorizedSignerForm(props.application?.signers),
    });

    const storedPowerOfAttorneyFileName = props.application?.signers
        ?.find(signer => signer.type === SignerType.AUTHORIZED_REPRESENTATIVE)
        ?.powerOfAttorneyFileName;

    const [representatives, setRepresentatives] = useState<SelectableRepresentative[]>([]);
    const [isLoadingRepresentatives, setIsLoadingRepresentatives] = useState(false);

    const getPetrolStationsCardDistributions = async () => {
        try {
            setIsLoadingStations(true);

            const res = await PetrolStationService.getStationsWithCardDistribution();
            setStations(res)
        } catch(e) {
            console.error(e);
        } finally {
            setIsLoadingStations(false);
        }
    }

    useEffect(() => {
        getPetrolStationsCardDistributions()
    }, [])

    useEffect(() => {
        if (!customerId) return;

        CustomerLocationService.getCustomerLocations(customerId)
            .then(setCustomerLocations)
            .catch(() => setCustomerLocations([]));
    }, [customerId])

    useEffect(() => {
        if (!customerId) return;

        setIsLoadingRepresentatives(true);
        CustomerService.getRepresentatives(customerId)
            .then(res => setRepresentatives(toSelectableRepresentatives(res ?? EMPTY_CUSTOMER_REPRESENTATIVES)))
            .catch(() => setRepresentatives([]))
            .finally(() => setIsLoadingRepresentatives(false));
    }, [customerId])

    useEffect(() => {
        setIsLoadingDpdParcelShops(true);
        DpdParcelShopService.getDpdParcelShops()
            .then((shops) => {
                setDpdParcelShops(shops);
            })
            .catch((e) => {
                setDpdParcelShops([]);
                console.error(e);
            })
            .finally(() => {
                setIsLoadingDpdParcelShops(false);
            });
    }, []);

    const confirmSubmit = () => {
        setValidated(false);
        const isValidForm = validateForm();
        setValidated(true);
        if (isValidForm) {
            setIsConfirmModalVisible(true);
        } else {
            setSaving(false);
        }
    };


    const isCardValid = (card: CardForm) => {
        if (!card.cardName.trim()) return false;
        if (isCustomerLocationRequired(customerLocations) && isNullOrUndefined(card.customerLocationId)) return false;
        if (
            isNullOrUndefined(card.cardCategory) ||
            (!isNullOrUndefined(card.monthlyLimit) && card.monthlyLimit!.trim().length > 0
                && !CARD_APPLICATION_MONTHLY_LIMIT_REGEX.test(card.monthlyLimit!.trim())) ||
            isNullOrUndefined(card.isVirtual)
        ) {
            return false;
        }
        return true;
    };

    const hasAnyCards = () => {
        return Object.values(CardCategory).some(category => form.cards[category].length > 0);
    };

    const isPickupRequired = () => {
        if (!hasAnyCards()) return true;
        return Object.values(CardCategory).some(category => form.cards[category].some(card => !card.isVirtual));
    };

    const isSignerValid = () => {
        if (form.signerType === SignerType.REPRESENTATIVE) {
            return areRepresentativeSignersValid(form.selectedSignerRepresentatives, user?.personalIdentificationCode);
        }

        return isAuthorizedSignerValid(form.authorizedSigner, storedPowerOfAttorneyFileName);
    };

    const buildSigner = (): CustomerApplicationSigner => {
        if (form.signerType === SignerType.REPRESENTATIVE) {
            return { type: SignerType.REPRESENTATIVE, representatives: form.selectedSignerRepresentatives };
        }

        return {
            type: SignerType.AUTHORIZED_REPRESENTATIVE,
            ...form.authorizedSigner
        };
    };

    const validateForm = () => {
        if (!isEmailValidAndPresent(form.contactEmail) || !isPhoneNumber(form.contactPhone)) return false;
        if (isPickupRequired() && !form.pickupLocation) return false;
        if (isPickupRequired() && !form.sameDeliveryContact
            && (!isEmailValidAndPresent(form.recipientEmail) || !isPhoneNumber(form.recipientPhone))) return false;
        if (!form.purchaseVolume) return false;
        if (!form.creditLimit) return false;
        if (!isEmailValidAndPresent(form.invoiceEmail)) return false;
        if (form.eInvoice && (!form.bankAccountNumber || !validateBankAccountNumber(form.bankAccountNumber))) return false;
        if (!isSignerValid()) return false;
        if (!hasAnyCards()) return false;
        for (const category of Object.values(CardCategory)) {
            for (const card of form.cards[category]) {
                if (!isCardValid(card)) return false;
            }
        }
        return true;
    };

    const handleSubmit = async () => {
        setSaving(true);
        setSavingError(false);

        if (!customerId || !customer || (customer && !customer.contactId)) return;

        let allCards: CardForm[] = [];
        for (const category of Object.values(CardCategory)) {
            allCards = allCards.concat(form.cards[category].map(card => ({ ...card, cardCategory: category })));
        }

        const pickupRequired = isPickupRequired();
        
        console.log()

        const request = {
            customerId: customerId,
            contactPersonId: props.application ? props.application.contact?.id : customer.contactId,
            customerApplicationType: CustomerApplicationType.CREDIT_APPLICATION,
            ...(pickupRequired && {
                pickupType: form.pickupType,
                pickupLocation: form.pickupLocation,
                pickupLocationId: form.pickupLocationId,
                recipientEmail: form.sameDeliveryContact ? undefined : form.recipientEmail.trim(),
                recipientPhone: form.sameDeliveryContact ? undefined : form.recipientPhone.trim(),
            }),
            invoiceEmail: form.invoiceEmail,
            eInvoice: form.eInvoice,
            bankAccountNumber: form.eInvoice ? form.bankAccountNumber : undefined,
            purchaseVolume: form.purchaseVolume,
            creditLimit: form.creditLimit,
            paymentDay: form.paymentDay,
            invoicesPerMonth: form.invoicesPerMonth,
            signer: buildSigner(),
            contactEmail: form.contactEmail,
            contactPhone: form.contactPhone,
            customerApplicationCards: allCards.map(card => ({
                cardName: card.cardName,
                cardLimit: card.monthlyLimit?.trim() ? Number(card.monthlyLimit) : undefined,
                cardCategory: card.cardCategory,
                cardType: ICustomerCardType.PAYMENT_CARD,
                isVirtual: card.isVirtual,
                customerLocationId: card.customerLocationId
            }))
        };

        try {
            if (isUpdate) {
                await CustomerApplicationService.update(props.application!.id, request);
            } else {
                const createdApplicationId = await CustomerApplicationService.create(request);

                dispatch({
                    type: "setCustomer",
                    value: {
                        ...state.customer,
                        hasApplications: true,
                        blockingApplications: withCreatedApplication(
                            state.customer.blockingApplications,
                            request.customerApplicationType,
                            createdApplicationId
                        )
                    }
                });
            }

            toast.success("Taotlus edukalt saadetud");
            setTimeout(() => { props.handleModalHide(); props.onSuccess?.(); }, 300);
        } catch (e) {
            setSavingError(true);
        } finally {
            setSaving(false);
        }
    };

    const pickupOptions = stations.map(station => ({ label : station.name, value: station.id }))
    const dpdOptions = dpdParcelShops.map(shop => ({ label: shop.companyName, value: shop.parcelShopId }));
    const isUpdate = !!props.application;

    return (
        <>
            <Modal
                show={true}
                size={"lg"}
                onHide={props.handleModalHide}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {
                            isUpdate ? `Krediidi taotluse #${props.application!.id} muutmine` : 'Krediidi taotlemine'
                        }
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Form noValidate validated={validated} onSubmit={(e: SyntheticEvent) => e.preventDefault()}
                        id={"card-form"}
                        className={'d-flex flex-column'}>
                        <div className="row">
                            <Form.Group controlId={'form-customer-name'} className={'col-md-6 mb-3'}>
                                <Form.Label>Kliendi nimi</Form.Label>
                                <Form.Control
                                    required
                                    value={customer.name}
                                    disabled
                                    type="text"
                                    placeholder="Kliendi nimi"
                                />
                            </Form.Group>
                            <Form.Group controlId={'form-customer-registry-number'} className={'col-md-6 mb-3'}>
                                <Form.Label>Registrikood</Form.Label>
                                <Form.Control
                                    required
                                    value={customer.registryNumber}
                                    disabled
                                    type="text"
                                    placeholder="Registrikood"
                                />
                            </Form.Group>
                            <h2 className='col-12'>Kontaktisiku andmed</h2>
                            <Form.Group controlId={'form-applicant-name'} className={'col-md-6 mb-3'}>
                                <Form.Label>Nimi</Form.Label>
                                <Form.Control
                                    required
                                    value={props.application?.contact?.name ?? user.name}
                                    disabled
                                    type="text"
                                />
                                <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={'form-contact-email'} className={'col-md-6 mb-3'}>
                                <Form.Label>E-post</Form.Label>
                                <Form.Control
                                    required
                                    value={form.contactEmail}
                                    onChange={e => setForm({ ...form, contactEmail: e.target.value })}
                                    disabled={saving}
                                    type="email"
                                    placeholder="E-post"
                                />
                                <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={'form-contact-phone'} className={'col-md-6 mb-3'}>
                                <Form.Label>Telefon</Form.Label>
                                <InputGroup className="col-12 p-0">
                                    <InputGroup.Prepend>
                                        <InputGroup.Text
                                            id="phone-number-prefix"
                                        >
                                            +372
                                        </InputGroup.Text>
                                    </InputGroup.Prepend>
                                    <Form.Control 
                                        required 
                                        minLength={1}
                                        value={form.contactPhone}
                                        onChange={e => setForm({ ...form, contactPhone: handleTextInputNumberChange(form.contactPhone, e.target.value) })}
                                        disabled={saving}
                                        type="tel"
                                        placeholder="Telefon"
                                        isInvalid={validated && !isPhoneNumber(form.contactPhone)}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {!form.contactPhone ? 'Kohustuslik väli' : 'Vigane telefoninumber'}
                                    </Form.Control.Feedback>
                                </InputGroup>
                            </Form.Group>
                            <SignerSection
                                signerType={form.signerType}
                                onSignerTypeChange={signerType => setForm({ ...form, signerType })}
                                representatives={representatives}
                                isLoadingRepresentatives={isLoadingRepresentatives}
                                selectedRepresentatives={form.selectedSignerRepresentatives}
                                onSelectedRepresentativesChange={selectedSignerRepresentatives =>
                                    setForm({ ...form, selectedSignerRepresentatives })
                                }
                                authorizedSigner={form.authorizedSigner}
                                onAuthorizedSignerChange={authorizedSigner => setForm({ ...form, authorizedSigner })}
                                currentUserPersonalCode={user?.personalIdentificationCode}
                                storedPowerOfAttorneyFileName={storedPowerOfAttorneyFileName}
                                applicationId={props.application?.id}
                                customerId={customerId ?? undefined}
                                disabled={saving}
                                validated={validated}
                            />
                            <h2 className='col-12'>Arveldamine</h2>
                            <Form.Group controlId={'form-credit-limit'} className={'col-md-6 mb-3'}>
                                <Form.Label>
                                    Taotletav krediidilimiit
                                    <OverlayTrigger
                                        trigger={["hover", "focus"]}
                                        placement="right"
                                        overlay={
                                            <Popover id="credit-limit-popover">
                                                <Popover.Content>1.5 kuu ligikaudne ostude summa</Popover.Content>
                                            </Popover>
                                        }
                                    >
                                        <span
                                            style={{ cursor: "pointer", marginLeft: 6, color: "#0d6efd" }}
                                            tabIndex={0}
                                            aria-label="Info"
                                        >
                                            &#9432;
                                        </span>
                                    </OverlayTrigger>
                                </Form.Label>
                                <Form.Control
                                    required
                                    value={form.creditLimit}
                                    onChange={e => setForm({ ...form, creditLimit: e.target.value })}
                                    disabled={saving}
                                    type="text"
                                    placeholder="Taotletav krediidilimiit"
                                />
                                <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={'form-purchase-volume'} className={'col-md-6 mb-3'}>
                                <Form.Label>Hinnanguline ostumaht kuus</Form.Label>
                                <Form.Control
                                    required
                                    value={form.purchaseVolume}
                                    onChange={e => setForm({ ...form, purchaseVolume: e.target.value })}
                                    disabled={saving}
                                    type="text"
                                    placeholder="Hinnanguline ostumaht kuus"
                                />
                                <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={'form-invoice-email'} className={'col-md-6 mb-3'}>
                                <Form.Label>Arve e-post</Form.Label>
                                <Form.Control
                                    required
                                    value={form.invoiceEmail}
                                    onChange={e => setForm({ ...form, invoiceEmail: e.target.value })}
                                    disabled={saving}
                                    type="email"
                                    placeholder="Arvelduse e-post"
                                />
                                <Form.Control.Feedback type="invalid">Kohustuslik väli</Form.Control.Feedback>
                            </Form.Group>
                            <Form.Group controlId={'form-payment-day'} className={'col-md-6 mb-3'}>
                                <Form.Label>Maksepäev</Form.Label>
                                <Form.Control
                                    value={form.paymentDay}
                                    disabled
                                    type="number"
                                    placeholder="Maksepäev"
                                />
                            </Form.Group>
                            <div className='col-md-6 mb-3'>
                                    <Form.Group controlId={'form-e-invoice'} className='mb-0'>
                                    <Form.Check
                                        type="checkbox"
                                        label="Soovin e-arvet (EDI)"
                                        checked={form.eInvoice}
                                        onChange={(e: { target: { checked: boolean; }; }) => setForm({ ...form, eInvoice: e.target.checked })}
                                        disabled={saving}
                                    />
                                </Form.Group>
                                {form.eInvoice && (
                                    <Form.Group controlId={'form-bank-account-number'} className='mb-1'>
                                        <Form.Label>Pangakonto number</Form.Label>
                                        <Form.Control
                                            required
                                            value={form.bankAccountNumber}
                                            onChange={e => setForm({ ...form, bankAccountNumber: e.target.value })}
                                            disabled={saving}
                                            type="text"
                                            placeholder="Pangakonto number"
                                            isInvalid={validated && !validateBankAccountNumber(form.bankAccountNumber)}
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {!form.bankAccountNumber ? 'Kohustuslik väli' : 'Vigane pangakonto number'}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                )}
                            </div>
                            <Form.Group controlId={'form-invoice-times'} className={'col-md-6 mb-3'}>
                                <Form.Label>Arveperioodid</Form.Label>
                                <div className="d-flex flex-column">
                                    <Form.Check
                                        type="radio"
                                        label="Üks kord kuus"
                                        name="invoicesPerMonth"
                                        id="invoicesPerMonth-once"
                                        checked={form.invoicesPerMonth === 1}
                                        onChange={() => setForm({ ...form, invoicesPerMonth: 1 })}
                                        disabled={saving}
                                    />
                                    <Form.Check
                                        type="radio"
                                        label={<>
                                            Kaks korda kuus
                                            <OverlayTrigger
                                                trigger={["hover", "focus"]}
                                                placement="right"
                                                overlay={
                                                    <Popover id="invoice-twice-popover">
                                                        <Popover.Content>Arve väljastatakse perioodi 1-14 ja 15-30/31 kohta</Popover.Content>
                                                    </Popover>
                                                }
                                            >
                                                <span
                                                    style={{ cursor: "pointer", marginLeft: 6, color: "#0d6efd" }}
                                                    tabIndex={0}
                                                    aria-label="Info"
                                                >
                                                    &#9432;
                                                </span>
                                            </OverlayTrigger>
                                        </>}
                                        name="invoicesPerMonth"
                                        id="invoicesPerMonth-twice"
                                        checked={form.invoicesPerMonth === 2}
                                        onChange={() => setForm({ ...form, invoicesPerMonth: 2 })}
                                        disabled={saving}
                                    />
                                </div>
                            </Form.Group>
                        </div>
                        <Form.Group controlId={'form-card-number'} className={'col-12 p-0'}>
                            <Form.Label>
                                <span className="text-nowrap">Kaardid</span>
                            </Form.Label>
                            <PaymentCardSection
                                cards={form.cards}
                                setCards={newCards => setForm({ ...form, cards: newCards })}
                                saving={saving}
                                categoryLabels={cardCategoryLabels}
                                categoryIcons={CARD_CATEGORY_TO_ICON}
                                customerLocations={customerLocations}
                                validated={validated}
                            />
                            {
                                validated && !hasAnyCards() &&
                                <div className="d-block text-left invalid-feedback u-fs-12">Palun lisage vähemalt üks kaart</div>
                            }
                        </Form.Group>
                        {isPickupRequired() && (
                            <>
                                <h2>Kohaletoimetamine</h2>
                                <Form.Group controlId={'form-card-pickup-location'} className={'col-12 p-0'}>
                                    <div className='d-flex'>
                                        <SwitchButton
                                            className="d-flex"
                                            labelLeft="Teenindusjaam"
                                            labelRight="DPD pakiautomaat"
                                            onActiveChange={active => {
                                                setForm({
                                                    ...form,
                                                    pickupType: active === SwitchButtonState.LEFT ? 'STATION' : 'DPD',
                                                    pickupLocation: '',
                                                    pickupLocationId: '',
                                                });
                                            }}
                                            active={form.pickupType === 'STATION' ? SwitchButtonState.LEFT : SwitchButtonState.RIGHT}
                                            size="md"
                                        />
                                    </div>
                                    <Form.Label className="mt-2">
                                        <span className="text-nowrap">Kättesaamise koht</span>
                                    </Form.Label>
                                    <div className='col-md-6 pl-0'>
                                        {form.pickupType === 'STATION' ? (
                                            <Select
                                                options={pickupOptions}
                                                value={pickupOptions.find(opt => opt.value === form.pickupLocationId) || null}
                                                classNamePrefix={'select'}
                                                onChange={(
                                                val: ValueType<PickupOption>
                                                ) => {
                                                const option = val as PickupOption | null;
                                                setForm({
                                                    ...form,
                                                    pickupLocation: option ? option.label : '',
                                                    pickupLocationId: option ? option.value : '',
                                                });
                                                }}
                                                className={`select-terminal ${!form.pickupLocation && validated ? 'invalid' : ''}`}
                                                placeholder="Vali teenindusjaam"
                                                noOptionsMessage={() => "Valikud puuduvad"}
                                                isLoading={isLoadingStations}
                                                loadingMessage={() => "Laen andmeid..."}
                                            />
                                        ) : (
                                            <Select
                                                options={dpdOptions}
                                                value={dpdOptions.find(opt => opt.value === form.pickupLocationId) || null}
                                                classNamePrefix={'select'}
                                                onChange={(
                                                val: ValueType<PickupOption>
                                                ) => {
                                                const option = val as PickupOption | null;
                                                setForm({
                                                    ...form,
                                                    pickupLocation: option ? option.label : '',
                                                    pickupLocationId: option ? option.value : '',
                                                });
                                                }}
                                                className={`select-terminal ${!form.pickupLocation && validated ? 'invalid' : ''}`}
                                                placeholder="Vali DPD pakiautomaat"
                                                noOptionsMessage={() => "Valikud puuduvad"}
                                                isLoading={isLoadingDpdParcelShops}
                                                loadingMessage={() => "Laen andmeid..."}
                                            />
                                        )}
                                    </div>
                                    {
                                        !form.pickupLocation && validated &&
                                        <div className="d-block text-left invalid-feedback">Kohustuslik väli</div>
                                    }
                                </Form.Group>
                                <Form.Group controlId={'form-same-delivery-contact'} className={'col-12 p-0 mt-2'}>
                                    <Form.Check
                                        type="checkbox"
                                        label="Kättetoimetamise kontaktid on samad tellija kontaktidega"
                                        checked={form.sameDeliveryContact}
                                        onChange={(e: { target: { checked: boolean; }; }) => setForm({
                                            ...form,
                                            sameDeliveryContact: e.target.checked,
                                            recipientEmail: '',
                                            recipientPhone: '',
                                        })}
                                        disabled={saving}
                                    />
                                </Form.Group>
                                {!form.sameDeliveryContact && (
                                    <div className="d-flex flex-wrap col-12 p-0 mt-2">
                                        <Form.Group controlId={'form-recipient-email'} className={'col-md-6 pl-0 mb-1'}>
                                            <Form.Label>Saaja e-post</Form.Label>
                                            <Form.Control
                                                required
                                                value={form.recipientEmail}
                                                onChange={e => setForm({ ...form, recipientEmail: e.target.value })}
                                                disabled={saving}
                                                type="text"
                                                placeholder="Saaja e-post"
                                                isInvalid={validated && !isEmailValidAndPresent(form.recipientEmail)}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {!form.recipientEmail ? 'Kohustuslik väli' : 'Vigane e-post'}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                        <Form.Group controlId={'form-recipient-phone'} className={'col-md-6 pr-0 mb-1'}>
                                            <Form.Label>Saaja telefon</Form.Label>
                                            <Form.Control
                                                required
                                                value={form.recipientPhone}
                                                onChange={e => setForm({ ...form, recipientPhone: e.target.value })}
                                                disabled={saving}
                                                type="text"
                                                placeholder="Saaja telefon"
                                                isInvalid={validated && !isPhoneNumber(form.recipientPhone)}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                {!form.recipientPhone ? 'Kohustuslik väli' : 'Vigane telefoninumber'}
                                            </Form.Control.Feedback>
                                        </Form.Group>
                                    </div>
                                )}
                            </>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1">
                    <Button variant="outline-dark"
                        onClick={() => props.handleModalHide()}
                        type="button"
                        disabled={saving}
                        className={"d-flex align-items-center"}>
                        <span className='px-2'>
                            {
                                isUpdate ? 'Tühista' : 'Loobu'
                            }
                        </span>
                    </Button>
                    <Button variant="primary" onClick={confirmSubmit} type="button" disabled={saving}
                        className="d-flex align-items-center">
                        {
                            !saving && (
                                <span className='px-2'>
                                    {
                                        isUpdate ? 'Salvesta' : 'Saada taotlus'
                                    }
                                </span>
                            )
                        }
                        {
                            saving &&
                            <>
                                <span className='px-2'>Salvestab…</span>
                                <Spinner size={"sm"} animation={"border"} />
                            </>
                        }
                    </Button>
                </Modal.Footer>
                {
                    !saving && savingError &&
                    <span className="d-flex justify-content-end text-danger px-2 pb-2">Viga salvestamisel, proovi mõne hetke pärast uuesti.</span>
                }
            </Modal>

            {
                validated && isConfirmModalVisible &&
                <ModifyCustomerCardConfirmModal
                    isUpdate={isUpdate}
                    handleConfirmSubmit={() => {
                        setIsConfirmModalVisible(false);
                        handleSubmit()
                    }}
                    handleConfirmReject={() => {
                        setSaving(false);
                        setIsConfirmModalVisible(false)
                    }}
                />
            }
        </>
    )
}